import { createFileRoute, Link } from '@tanstack/react-router'
import { Printer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useUpdateDispatch, useVoidDispatch } from '@/api/dispatches'
import type { DispatchDetail, DispatchMovement } from '@/api/dispatches'
import { useIdempotencyKey } from '@/api/idempotency'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// Dispatch detail (UI-SCREENS I-06, 05c #171): the Dispatch number, the channel and date, the
// lines and the movements it posted (Home down, the channel up), the note editor, the print-
// friendly page and the Owner's Void dialog — which lists every blocking item with the records to
// void first when the reversal is refused.
export const Route = createFileRoute('/w/$slug/dispatches/$dispatchId/')({
  component: DispatchDetailScreen,
  staticData: { breadcrumb: 'dispatches.detail.title' },
})

function DispatchDetailScreen() {
  const { t } = useTranslation()
  const { slug, dispatchId } = Route.useParams()
  const id = Number(dispatchId)
  const dispatch = useDispatch(slug, id)
  const voidDispatch = useVoidDispatch()
  const idempotency = useIdempotencyKey()
  const updateDispatch = useUpdateDispatch()

  if (dispatch.isPending) return <LoadingSkeleton variant="page" />
  if (dispatch.isError || !dispatch.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data: DispatchDetail = dispatch.data
  const createdBy = data.created_by ? (data.created_by.name ?? data.created_by.email) : '—'

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidDispatch.mutate(
      { params: { path: { slug, dispatch_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateDispatch.mutate(
      { params: { path: { slug, id } }, body: { dispatch: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-3xl flex-col gap-4 ${voidedClass(Boolean(data.voided_at))}`}>
      <Link to="/w/$slug/dispatches" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('dispatches.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-xl font-semibold">{data.dispatch_no}</h1>
        <VoidedBadge record={data} />
        <div className="ml-auto flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-11">
            <Link to="/w/$slug/dispatches/$dispatchId/print" params={{ slug, dispatchId: String(data.id) }}>
              <Printer aria-hidden="true" />
              {t('dispatches.detail.print')}
            </Link>
          </Button>
          {data.can.void ? (
            <VoidDialog
              onVoid={onVoid}
              pending={voidDispatch.isPending}
              errorMessage={voidDispatch.isError ? correctionErrorMessage(t, voidDispatch.error) : ''}
              onReset={voidDispatch.reset}
              blocked={voidDispatch.isError ? <DependentRecords slug={slug} error={voidDispatch.error} /> : null}
            />
          ) : null}
        </div>
      </div>
      <VoidedNotice record={data} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <Figure label={t('dispatches.columns.channel')} value={data.sales_channel.name} />
        <Figure label={t('dispatches.columns.date')} value={formatBusinessDate(data.occurred_on)} />
        <Figure label={t('dispatches.columns.units')} value={t('dispatches.units', { count: data.total_units })} />
        <Figure label={t('dispatches.columns.createdBy')} value={createdBy} />
      </dl>

      <NoteCard
        note={data.note}
        lockVersion={data.lock_version}
        canEdit={data.can.edit_note}
        pending={updateDispatch.isPending}
        errorMessage={updateDispatch.isError ? correctionErrorMessage(t, updateDispatch.error) : ''}
        onSave={onSaveNote}
        onReset={updateDispatch.reset}
      />

      <h2 className="font-heading text-lg font-semibold">{t('dispatches.detail.lines')}</h2>
      <Card>
        <CardContent className="py-4">
          <ul className="flex flex-col divide-y">
            {data.lines.map((line) => (
              <li key={line.id} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
                <span className="font-medium">
                  {line.variant.sku} · {line.variant.name}
                </span>
                <span>{t('dispatches.units', { count: line.qty })}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <h2 className="font-heading text-lg font-semibold">{t('dispatches.detail.movements')}</h2>
      <Card>
        <CardContent className="py-4">
          <ul className="flex flex-col divide-y">
            {data.movements.map((movement) => (
              <li key={movement.id} className="py-3 first:pt-0 last:pb-0">
                <MovementRow dispatch={data} movement={movement} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  )
}

// A Variant leaving Home or arriving at the channel. A void adds the exact negation of each row,
// linked to it by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ dispatch, movement }: { dispatch: DispatchDetail; movement: DispatchMovement }) {
  const { t } = useTranslation()
  const place =
    movement.location.kind === 'home' ? t('dispatches.detail.home') : dispatch.sales_channel.name

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium">
        {movement.variant ? `${movement.variant.sku} · ${movement.variant.name}` : '—'}
      </span>
      <span className="text-muted-foreground">{place}</span>
      {movement.reverses_movement_id ? (
        <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
      ) : null}
      <span className="ml-auto">{signed(movement.qty_delta)}</span>
    </div>
  )
}

const signed = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatQty(Math.abs(value))}`

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
